import { useCallback, useMemo } from 'react';
import { DateTime } from 'luxon';
import useAgentTimeZone from 'hooks/useAgentTimeZone';
import { useParams } from 'react-router-dom';
import { BarChart3, Calendar as CalendarIcon } from 'lucide-react';
import Footer from 'components/Footer';
import useSearchFilters from 'hooks/useSearchFilters';
import useBusinessHealthInsights from './hooks/useBusinessHealthInsights';
import {
    PageContainer,
    Header,
    Title,
    Actions,
    ReportDateField,
    ReportDateLabel,
    EmptyStateCard,
    EmptyStateIconContainer,
    EmptyStateText,
    StyledReportDatePicker,
} from './styled';
import BusinessHealthWidget from './BusinessHealthWidget';
import InsightSectionWidget from './InsightSectionWidget';
import isEmptyBusinessHealthData from './helpers/isEmptyBusinessHealthData';

const BusinessHealthInsights = () => {
    const { agentId = '' } = useParams();
    const agentTimeZone = useAgentTimeZone();
    const { values, setValue } = useSearchFilters({ filterNames: ['date'] });

    const dateParam = values?.date as string | undefined;

    const reportDateValue = useMemo(() => {
        const now = DateTime.now();
        if (!dateParam) return now.toJSDate();
        return DateTime.fromISO(dateParam).toJSDate();
    }, [dateParam]);

    const handleReportDateChange = useCallback(
        (date: Date) => {
            const tmpDate = new Date(date);
            const current = DateTime.now().setZone(agentTimeZone).toJSDate();
            tmpDate.setHours(current.getHours());
            tmpDate.setMinutes(current.getMinutes());
            tmpDate.setSeconds(current.getSeconds());
            tmpDate.setMilliseconds(current.getMilliseconds());
            setValue('date', DateTime.fromJSDate(tmpDate).toJSDate().toISOString());
        },
        [setValue, agentTimeZone],
    );
    const {
        data,
        isLoading,
    } = useBusinessHealthInsights({
        agentId,
        date: dateParam,
    });

    const showEmptyState = !isLoading && isEmptyBusinessHealthData(data);

    return (
        <PageContainer>
            <Header>
                <Title>Business Insights</Title>
                <Actions>
                    <ReportDateField>
                        <ReportDateLabel htmlFor="business-health-report-end-date">
                            Ending on:
                        </ReportDateLabel>
                        <StyledReportDatePicker
                            id="business-health-report-end-date"
                            hideLabel
                            selectionType="single"
                            value={reportDateValue}
                            onChange={handleReportDateChange}
                            maxDate={new Date()}
                            placeholder="Select date"
                            icon={<CalendarIcon aria-hidden />}
                        />
                    </ReportDateField>
                </Actions>
            </Header>
            {showEmptyState ? (
                <EmptyStateCard>
                    <EmptyStateIconContainer>
                        <BarChart3 aria-hidden />
                    </EmptyStateIconContainer>
                    <EmptyStateText>There is no data for this report.</EmptyStateText>
                </EmptyStateCard>
            ) : (
                <>
                    <BusinessHealthWidget isLoading={isLoading} data={data} />
                    <InsightSectionWidget title="Problems" status="critical" isLoading={isLoading} data={data} />
                    <InsightSectionWidget title="Needs Attention" status="at_risk" isLoading={isLoading} data={data} />
                    <InsightSectionWidget title="Healthy" status={['healthy', 'stable']} isLoading={isLoading} data={data} />
                </>
            )}
            <Footer />
        </PageContainer>
    );
};

export default BusinessHealthInsights;
